import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  Textarea,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { adminCreateLink, adminUpdateLink } from "apps/project-iq/api";
import {
  DIAGNOSTIC_RUN_CATEGORY,
  LINK_CATEGORY_LABELS,
  LinkCategory,
  SUBTEXT_CATEGORIES,
} from "apps/project-iq/models/IAdminLink";
import { DiagnosticMethod } from "apps/project-iq/models/IDiagnostic";
import {
  ALL_DIAGNOSTIC_METHODS,
  METHOD_LABELS,
} from "apps/project-iq/utils/diagnosticUtils";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { useEffect, useState } from "react";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedDropdownStyles,
} from "theme/sharedStyles";
import { useManageIqLinksStyles } from "./ManageIqLinks.styles";
import { IEditLinkDialogProps } from "./ManageIqLinks.types";

const ALL_LINK_CATEGORIES = (
  Object.keys(LINK_CATEGORY_LABELS) as LinkCategory[]
).sort((a, b) =>
  LINK_CATEGORY_LABELS[a].localeCompare(LINK_CATEGORY_LABELS[b]),
);

export const EditLinkDialog = ({
  open,
  link,
  existingDiagnosticLinkTypes,
  onSave,
  onDismiss,
}: IEditLinkDialogProps): JSX.Element => {
  const styles = useManageIqLinksStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();
  const dropdownStyles = useSharedDropdownStyles();

  const isCreateMode = link === null;

  const [category, setCategory] = useState<LinkCategory | "">("");
  const [linkType, setLinkType] = useState<DiagnosticMethod | "">("");
  const [displayName, setDisplayName] = useState<string>("");
  const [url, setUrl] = useState<string>("");
  const [subtext, setSubtext] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (link) {
      setCategory(link.category);
      setLinkType(link.linkType ?? "");
      setDisplayName(link.displayName);
      setUrl(link.url);
      setSubtext(link.subtext ?? "");
    } else {
      setCategory("");
      setLinkType("");
      setDisplayName("");
      setUrl("");
      setSubtext("");
    }
    setFieldErrors({});
    setIsSaving(false);
  }, [link, open]);

  const shouldShowLinkType = category === DIAGNOSTIC_RUN_CATEGORY;
  const shouldShowDetails = !!category && SUBTEXT_CATEGORIES.has(category);

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (isCreateMode && !category) errors.category = "Category is required";
    if (shouldShowLinkType && !linkType)
      errors.linkType = "Link type is required";
    if (!displayName.trim())
      errors.displayName = "Link display name is required";
    if (shouldShowDetails && !subtext.trim())
      errors.subtext = "Subtext is required";
    if (!url.trim()) {
      errors.url = "Link URL is required";
    } else {
      try {
        const parsed = new URL(url.trim());
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
          errors.url = "Link URL must use http or https";
        }
      } catch {
        errors.url = "Link URL is not a valid URL";
      }
    }
    return errors;
  };

  const handleSave = async (): Promise<void> => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setIsSaving(true);
    try {
      if (isCreateMode) {
        if (!category) throw new Error("Category is required");
        await adminCreateLink({
          category,
          linkType: linkType || null,
          displayName: displayName.trim(),
          url: url.trim(),
          subtext: shouldShowDetails ? subtext.trim() || null : null,
        });
      } else {
        if (!link) throw new Error("No link to save");
        await adminUpdateLink(link.id, {
          displayName: displayName.trim(),
          url: url.trim(),
          subtext: shouldShowDetails ? subtext.trim() || null : null,
        });
      }
      onSave();
    } catch {
      setFieldErrors((prev) => ({
        ...prev,
        general: "Save failed. Please try again.",
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCategoryChange = (newCategory: LinkCategory): void => {
    setCategory(newCategory);
    setLinkType("");
    if (!SUBTEXT_CATEGORIES.has(newCategory)) setSubtext("");
    if (fieldErrors.category) {
      setFieldErrors((prev) => ({ ...prev, category: "" }));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open) onDismiss();
      }}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={onDismiss}
              />
            }
          >
            {isCreateMode ? "Add Link" : "Edit Link"}
          </DialogTitle>
          <DialogContent>
            <div className={contentStyles.formContainer}>
              {isSaving && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label="Saving link..." />
                </div>
              )}
              {fieldErrors.general && (
                <div role="alert" className={styles.errorAlert}>
                  {fieldErrors.general}
                </div>
              )}
              {isCreateMode ? (
                <Field
                  label="Category"
                  required
                  validationState={fieldErrors.category ? "error" : "none"}
                  validationMessage={fieldErrors.category}
                  className={styles.dialogField}
                >
                  <Dropdown
                    placeholder="Select a category..."
                    value={category ? LINK_CATEGORY_LABELS[category] : ""}
                    selectedOptions={category ? [category] : []}
                    onOptionSelect={(_, d) => {
                      handleCategoryChange(d.optionValue as LinkCategory);
                    }}
                    className={dropdownStyles.dropdown}
                    listbox={{ className: dropdownStyles.listbox }}
                  >
                    {ALL_LINK_CATEGORIES.map((cat) => (
                      <Option
                        key={cat}
                        value={cat}
                        text={LINK_CATEGORY_LABELS[cat]}
                      >
                        {LINK_CATEGORY_LABELS[cat]}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              ) : (
                <Field label="Category" className={styles.dialogField}>
                  <Input
                    value={link ? LINK_CATEGORY_LABELS[link.category] : ""}
                    readOnly
                    disabled
                  />
                </Field>
              )}
              {shouldShowLinkType &&
                (isCreateMode ? (
                  <Field
                    label="Link Type"
                    required
                    validationState={fieldErrors.linkType ? "error" : "none"}
                    validationMessage={fieldErrors.linkType}
                    className={styles.dialogField}
                  >
                    <Dropdown
                      placeholder="Select a link type..."
                      value={
                        linkType
                          ? METHOD_LABELS[linkType as DiagnosticMethod]
                          : ""
                      }
                      selectedOptions={linkType ? [linkType] : []}
                      onOptionSelect={(_, d) => {
                        setLinkType(d.optionValue as DiagnosticMethod);
                        if (fieldErrors.linkType)
                          setFieldErrors((prev) => ({ ...prev, linkType: "" }));
                      }}
                      className={dropdownStyles.dropdown}
                      listbox={{ className: dropdownStyles.listbox }}
                    >
                      {ALL_DIAGNOSTIC_METHODS.map((method) => {
                        const isAlreadyConfigured =
                          existingDiagnosticLinkTypes.includes(method);
                        return (
                          <Option
                            key={method}
                            value={method}
                            text={METHOD_LABELS[method]}
                            disabled={isAlreadyConfigured}
                          >
                            {METHOD_LABELS[method]}
                            {isAlreadyConfigured ? " (already configured)" : ""}
                          </Option>
                        );
                      })}
                    </Dropdown>
                  </Field>
                ) : (
                  <Field label="Link Type" className={styles.dialogField}>
                    <Input
                      value={
                        link && link.linkType
                          ? METHOD_LABELS[link.linkType]
                          : ""
                      }
                      readOnly
                      disabled
                    />
                  </Field>
                ))}
              <Field
                label="Link Display Name"
                required
                validationState={fieldErrors.displayName ? "error" : "none"}
                validationMessage={fieldErrors.displayName}
                className={styles.dialogField}
              >
                <Input
                  value={displayName}
                  onChange={(_, d) => {
                    setDisplayName(d.value);
                    if (fieldErrors.displayName)
                      setFieldErrors((prev) => ({
                        ...prev,
                        displayName: "",
                      }));
                  }}
                  placeholder="e.g., NH Diagnostic IQ Hub Page"
                />
              </Field>
              {shouldShowDetails && (
                <Field
                  label="Subtext"
                  required
                  validationState={fieldErrors.subtext ? "error" : "none"}
                  validationMessage={fieldErrors.subtext}
                  className={styles.dialogField}
                >
                  <Textarea
                    value={subtext}
                    onChange={(_, d) => {
                      setSubtext(d.value);
                      if (fieldErrors.subtext)
                        setFieldErrors((prev) => ({ ...prev, subtext: "" }));
                    }}
                    placeholder="e.g., Use this link to access the NH Diagnostic IQ hub"
                    resize="vertical"
                  />
                </Field>
              )}
              <Field
                label="Link URL"
                required
                validationState={fieldErrors.url ? "error" : "none"}
                validationMessage={fieldErrors.url}
                className={styles.dialogField}
              >
                <Input
                  value={url}
                  onChange={(_, d) => {
                    setUrl(d.value);
                    if (fieldErrors.url)
                      setFieldErrors((prev) => ({ ...prev, url: "" }));
                  }}
                  onBlur={() => {
                    if (!url.trim()) return;
                    try {
                      const parsed = new URL(url.trim());
                      if (
                        parsed.protocol !== "http:" &&
                        parsed.protocol !== "https:"
                      ) {
                        setFieldErrors((prev) => ({
                          ...prev,
                          url: "Link URL must use http or https",
                        }));
                      }
                    } catch {
                      setFieldErrors((prev) => ({
                        ...prev,
                        url: "Link URL is not a valid URL",
                      }));
                    }
                  }}
                  placeholder="https://..."
                />
              </Field>
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular />}
            tooltip="Save"
            dimmed={
              !displayName.trim() ||
              !url.trim() ||
              (isCreateMode && !category) ||
              (shouldShowLinkType && !linkType) ||
              (shouldShowDetails && !subtext.trim())
            }
            disabled={isSaving}
            onClick={() => void handleSave()}
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
